// People (parts/People.body): once per store (D103); the classes of R2; a Cobrain user is
// a person with a live principal, and "left" is what the store records of a leaver (a
// departed login, D94; a departed principal; an imported mailbox, D85). Chris Cousin's
// mailbox is a record Mike reads, not a colleague: nothing here notifies anybody.
//
// The find searches as you type, over the name and every address (pipeline F1, B1950). By company lists every
// company with a person on record, from the API, for the whole store, and can be typed into; it used to list only the
// companies of the 100 people on the page drawn. The find, the chips and the company are kept in the address. The list
// wears the filter bar every list wears (pipeline F5, B1957), with By owner: the owner of the person's company.
// Rights potential and Suppliers are read from Cobrain's own record of the company, never HubSpot (BUGS.md D137).
//
// With Personal on (BUGS.md D74) the page asks each store and merges the answers here (D12, D43: the stores are never
// joined in the database), as Tasks and Everything read do: a person of the personal store is drawn in violet with the
// private pill and opens their page in that store, and the counts are the stores' sums. Each store is paged on its own
// offset; a page is the first hundred of the stores' pages in name order, and Next carries each store on by what it gave.
import { Chip, FilterBar as FindBar } from '../parts/index.js';
import { useEffect, useState } from 'react';
import { records, type CompanyOption, type LastContact, type PeopleCounts, type PeopleFilter, type PersonRow, type Store } from '../api.js';
import { oneOf, useFilterParams } from '../find/params.js';
import { classWord, contactWord, Count, Empty, Failed, Foot, Heading, Loading, orText, personPath, Pill, RowLink, useLoad } from './shared.js';

const FILTERS: readonly PeopleFilter[] = ['all', 'clients', 'rights_holders', 'suppliers', 'team'];
const KEYS = ['q', 'show', 'company', 'owner'] as const;

/** A person with the store they are on record in. */
export interface PeopleRow extends PersonRow { store: Store; private: boolean }
export interface StorePage { store: Store; people: PersonRow[]; total: number; limit: number; counts: PeopleCounts; companies: CompanyOption[]; owners: CompanyOption[] }

/**
 * Each store's page merged into one in name order, `limit` at most. It takes only from the front of each store's page, so
 * what a store gave is always the first of its page whatever order the comparison finds; `taken` is how many each gave.
 */
export function mergePeople(pages: readonly StorePage[], limit: number): { rows: PeopleRow[]; taken: number[] } {
  const at = pages.map(() => 0);
  const rows: PeopleRow[] = [];
  while (rows.length < limit) {
    let pick = -1;
    for (let i = 0; i < pages.length; i++) {
      const head = pages[i]!.people[at[i]!];
      if (!head) continue;
      const best = pick < 0 ? null : pages[pick]!.people[at[pick]!]!;
      if (!best || head.name.localeCompare(best.name, 'en-GB') < 0) pick = i;
    }
    if (pick < 0) break;
    const page = pages[pick]!;
    rows.push({ ...page.people[at[pick]!]!, store: page.store, private: page.store.kind === 'personal' });
    at[pick] = at[pick]! + 1;
  }
  return { rows, taken: at };
}

/** The stores' counts summed: each pill and heading figure is the sum of what each store says. */
export function sumCounts(pages: readonly { counts: PeopleCounts }[]): PeopleCounts {
  const out: PeopleCounts = { all: 0, clients: 0, rightsHolders: 0, suppliers: 0, team: 0 };
  for (const p of pages) for (const k of Object.keys(out) as Array<keyof PeopleCounts>) out[k] += p.counts[k];
  return out;
}

export function People({ stores, open }: { stores: Store[]; open: (path: string) => void }) {
  const [p, set] = useFilterParams(KEYS);
  const { q, company, owner } = p;
  const filter = oneOf(p.show, FILTERS, 'all');
  const key = stores.map((s) => s.tenantId).join('+');
  // Each store's offset, and the offsets of the pages before it, for Previous. A change of stores starts again.
  const [paging, setPaging] = useState<{ key: string; offsets: number[]; back: number[][] }>({ key, offsets: stores.map(() => 0), back: [] });
  const offsets = paging.key === key ? paging.offsets : stores.map(() => 0);
  const back = paging.key === key ? paging.back : [];
  const restart = () => setPaging({ key, offsets: stores.map(() => 0), back: [] });
  useEffect(() => { if (paging.key !== key) restart(); }, [key]);   // eslint-disable-line react-hooks/exhaustive-deps
  const [state, , busy] = useLoad(() => Promise.all(stores.map((s, i) => records.people(s.tenantId, { q, filter, company, owner, offset: offsets[i] ?? 0 })
    .then((r): StorePage => ({ store: s, people: r.people, total: r.total, limit: r.limit, counts: r.counts, companies: r.facets?.companies ?? [], owners: r.facets?.owners ?? [] })))),
  [key, q, filter, company, owner, offsets.join(',')], { keep: true });
  const [contact] = useLoad<Record<string, LastContact>>(() => Promise.all(stores.map((s) => records.lastContact(s.tenantId)))
    .then((rs) => Object.assign({}, ...rs.map((r) => r.people)) as Record<string, LastContact>), [key]);
  const lc = contact.kind === 'ready' ? contact.data : null;
  const pages = state.kind === 'ready' ? state.data : null;
  const counts = pages ? sumCounts(pages) : null;
  const byName = (a: CompanyOption, b: CompanyOption) => a.name.localeCompare(b.name, 'en-GB');
  const companies = pages ? pages.flatMap((x) => x.companies).sort(byName) : null;
  const owners = pages ? pages.flatMap((x) => x.owners).sort(byName) : null;
  const limit = pages?.[0]?.limit ?? 100;
  const merged = pages ? mergePeople(pages, limit) : null;
  const total = pages ? pages.reduce((n, x) => n + x.total, 0) : 0;
  const offset = offsets.reduce((n, x) => n + x, 0);
  const onPage = (next: number) => {
    if (!merged) return;
    if (next > offset) setPaging({ key, offsets: offsets.map((o, i) => o + (merged.taken[i] ?? 0)), back: [...back, offsets] });
    else setPaging({ key, offsets: back[back.length - 1] ?? stores.map(() => 0), back: back.slice(0, -1) });
  };
  const choose = (f: PeopleFilter) => { set({ show: f === 'all' ? '' : f }); restart(); };
  return (
    <div className="recs">
      <Heading title="People" line={<>
        <Count n={counts?.all}>on record</Count>
        <Count n={counts?.clients}>at clients</Count>
        <Count n={counts?.team}>on the team</Count>
      </>} />
      <FindBar find="Find a person" q={q} onFind={(v) => { set({ q: v }); restart(); }}
        shown={pages && !busy ? total : null}   // BUGS.md D25: no narrowed count from the list still on screen while the next is read
        total={counts?.all ?? null} noun={['person', 'people']}
        onClearAll={() => { set({ q: '', show: '', company: '', owner: '' }); restart(); }}
        extraSet={filter !== 'all'}
        extra={<>
          <span className="chips-l">Show</span>
          <Pill on={filter === 'all'} onClick={() => choose('all')}>All</Pill>
          <Pill on={filter === 'clients'} onClick={() => choose('clients')} n={counts?.clients}>Clients</Pill>
          {/* BUGS.md D137: "Rights potential", Mike's name (7 Oct), for people at a company in the rights master. The
              filter's key stays `rights_holders` so a link someone kept with ?show=rights_holders still opens it. */}
          <Pill on={filter === 'rights_holders'} onClick={() => choose('rights_holders')} n={counts?.rightsHolders}>Rights potential</Pill>
          <Pill on={filter === 'suppliers'} onClick={() => choose('suppliers')} n={counts?.suppliers}>Suppliers</Pill>
          <Pill on={filter === 'team'} onClick={() => choose('team')} n={counts?.team}>The team</Pill>
        </>}
        picks={[
          { label: 'By company', find: 'Find a company', any: 'Any company', value: company, options: companies, onChange: (id) => { set({ company: id }); restart(); } },
          { label: 'By owner', find: 'Find a person', any: 'Anyone', value: owner, options: owners, onChange: (id) => { set({ owner: id }); restart(); } },
        ]} />
      {state.kind === 'loading' ? <Loading what="the people" /> : state.kind === 'error' ? <Failed message={state.message} /> : (
        <div className={busy ? 'scroll reading' : 'scroll'} aria-busy={busy}><table className="rec">
          <thead><tr><th>Person</th><th>Company</th><th>Title</th><th>Class</th><th>Addresses</th><th>Last contact</th><th>Open with them</th></tr></thead>
          <tbody>
            {merged!.rows.length === 0
              ? <Empty cols={7} head="Nobody matches that." says="Clear the search, or choose All. People come from the register and from every address Cobrain has read in your mail." />
              : null}
            {merged!.rows.map((p) => (
              <tr key={`${p.store.tenantId}-${p.id}`} className={p.private ? 'row-open row-private' : 'row-open'} onClick={() => open(personPath(p.id, p.private ? p.store.tenantId : null))}>
                <td><b><RowLink to={personPath(p.id, p.private ? p.store.tenantId : null)} open={open}>{p.name}</RowLink></b>{p.private ? <> <Chip className="private">Private · only you</Chip></> : null}{p.isUser ? <> <Chip>Cobrain user</Chip></> : null}{p.left ? <> <Chip>left</Chip></> : null}</td>
                {/* The Companies page reads the work store, so a personal person's company is named and opens nothing. */}
                <td>{p.companyName ? (p.private ? p.companyName : <RowLink className="dotted" to={`/companies/${p.companyId}`} open={open}>{p.companyName}</RowLink>) : ''}</td>
                <td>{orText(p.jobTitle)}</td>
                <td>{classWord(p.counterpartyClass)}{p.left ? ', departed' : ''}</td>
                <td>{p.addresses.join(', ')}</td>
                <td>{lc ? contactWord(lc[p.id]) : <span className="note">…</span>}</td>
                <td>{p.openWithThem > 0 ? `${p.openWithThem} open` : ''}</td>
              </tr>
            ))}
            <Foot cols={7} offset={offset} limit={limit} shown={merged!.rows.length} total={total} onPage={onPage} />
          </tbody>
        </table></div>
      )}
    </div>
  );
}
